import { useEffect, useState } from 'react'

/**
 * Whether the pane is wide enough to edit in.
 *
 * The same `(min-width: 768px)` boundary `ThreadSourcesDrawer` docks at: below
 * it the drawer covers the thread as a full-screen sheet, and the gate is
 * read-and-approve only. Editing a markdown body through a soft keyboard on a
 * covered screen is not a smaller version of the desktop gate, it is a worse
 * one, so the editor is ABSENT rather than shrunk — the approve path stays
 * whole on a phone.
 *
 * matchMedia, not a `md:` class, and for the drawer's reason: the decision is
 * behavioural (a control that does or does not exist), not cosmetic, and a
 * CSS-hidden Edit button is still focusable and still clickable.
 */
const WIDE_QUERY = '(min-width: 768px)'

function matchesWideQuery(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(WIDE_QUERY).matches
  )
}

export function useWidePane(): boolean {
  const [wide, setWide] = useState(matchesWideQuery)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }
    const query = window.matchMedia(WIDE_QUERY)
    const onChange = () => setWide(query.matches)
    onChange()
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  return wide
}
